import { FC, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useTranslations } from '@altermaind/core/i18n';
import { createGeneralCall } from '@altermaind/core/requests-status-management';
import {
  Box,
  Colors,
  DropDown,
  ItemInfo,
  Loader,
  RenderCallback,
  TextInput,
  prepareItemsForDropDown,
  useStyle,
  useThemeValue,
} from '@altermaind/web/ui';
import { getModeTheme } from '@/_shared/styles/state/selectors';
import { SelectItem } from '../types';
import { WrappedInput } from '../WrappedInput';

const SEARCH_VALUE = '__SEARCH__';
const LOAD_MORE_VALUE = '__LOAD_MORE__';
const LOADING_VALUE = '__LOADING__';
const DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 10;

type DropdownPaginatedCall = ReturnType<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  typeof createGeneralCall<any, any, any, { [key: string]: any; data: any; totalItems: any }>
>;

type Props = {
  control: Control<FieldValues>;
  name: string;
  label?: string;
  placeholder?: string;
  call: DropdownPaginatedCall;
  /**
   * Converte un item grezzo restituito dalla `call` nel formato `SelectItem` atteso dalla dropdown.
   * Permette al componente di essere riutilizzato con qualsiasi API senza conoscere la forma dei dati.
   *
   * @example
   * dataMapper={e => ({ label: `${e.firstName} ${e.lastName}`, value: e.id ?? '' })}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataMapper: (item: any) => SelectItem;
  pageSize?: number;
  hasBorderColor?: boolean;
  backgroundColorHeader?: keyof Colors;
  onItemSelect?: (item: SelectItem) => void;
  searchable?: boolean;
  alwaysShowLabel?: boolean;
};

/**
 * Dropdown con paginazione server-side e ricerca integrata.
 *
 * Riceve direttamente una `call` (creata con `createGeneralCall`) e un `dataMapper` per trasformare
 * i dati dell'API in `SelectItem`. Gestisce internamente accumulo delle pagine, debounce della ricerca
 * e stato di caricamento.
 *
 * @example
 * <WrappedDropdownPaginated
 *   control={control}
 *   name="manager"
 *   call={getUnassignedEmployeesCall}
 *   dataMapper={e => ({ label: `${e.firstName} ${e.lastName}`, value: e.id ?? '' })}
 *   pageSize={10}
 *   searchable
 * />
 */
export const WrappedDropdownPaginated: FC<Props> = ({
  control,
  name,
  label,
  placeholder,
  call,
  dataMapper,
  pageSize = DEFAULT_PAGE_SIZE,
  hasBorderColor = false,
  backgroundColorHeader,
  searchable = false,
  onItemSelect,
  alwaysShowLabel,
}) => {
  const t = useTranslations('inputs');
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [start, setStart] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [accumulatedItems, setAccumulatedItems] = useState<any[]>([]);
  const themeMode = useSelector(getModeTheme());
  const color: keyof Colors = 'primary/text';
  const parsedColor = useStyle({ color });
  const backgroundColorHeaderDefault = useThemeValue('colors', 'secondary/background');

  const { execute: executeCall, data, loading: loadingCall, totalItems } = call.hook();

  const isSearchResetRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const items: SelectItem[] = useMemo(
    () => accumulatedItems.map(dataMapper).filter((item: SelectItem) => item.value),
    [accumulatedItems, dataMapper],
  );

  const hasMore = totalItems > 0 && accumulatedItems.length < totalItems;

  const allItems: SelectItem[] = useMemo(() => {
    const isLoadingWithNoItems = loadingCall && items.length === 0;
    return [
      ...(searchable ? [{ label: '', value: SEARCH_VALUE }] : []),
      ...(isLoadingWithNoItems
        ? [{ label: '', value: LOADING_VALUE }]
        : [...items, ...(hasMore ? [{ label: t('loadMore'), value: LOAD_MORE_VALUE }] : [])]),
    ];
  }, [searchable, items, hasMore, loadingCall, t]);

  const preparedItems = useMemo(
    () => prepareItemsForDropDown([...allItems], { defaultSelected: [] }),
    [allItems],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      isSearchResetRef.current = true;
      setSearchQuery(value);
      setStart(0);
    }, DEBOUNCE_MS);
  }, []);

  const handleLoadMore = useCallback(() => {
    setStart(prev => prev + pageSize);
  }, [pageSize]);

  const contentExtractor: RenderCallback<SelectItem, ItemInfo> = ({ item }) => ({
    text: item.value === SEARCH_VALUE || item.value === LOADING_VALUE ? '' : item.label,
    textColor: item.value === LOAD_MORE_VALUE ? ('grey/light' as keyof Colors) : 'primary/text',
    checkBox: false,
    disabled: item.value === SEARCH_VALUE || item.value === LOADING_VALUE,
  });

  const renderItem: RenderCallback<SelectItem, ReactElement | string> = ({ item }) => {
    if (item.value === LOADING_VALUE) {
      return (
        <Box display='flex' justifyContent='center' padding='s'>
          <Loader size='s' />
        </Box>
      );
    }

    if (item.value === LOAD_MORE_VALUE) {
      return <Box style={{ cursor: 'pointer' }}>{item.label}</Box>;
    }

    if (item.value !== SEARCH_VALUE) {
      return item.label;
    }

    return (
      <Box onClick={e => e.stopPropagation()}>
        <TextInput
          inputRef={searchInputRef}
          value={searchValue}
          onChange={e => handleSearchChange(e.target.value)}
          placeholder={t('placeholderSearch')}
          borderColor=''
          borderWidth={1}
          borderRadius={5}
          padding='xxs'
          background='transparent'
          width='100%'
          maxLength={80}
          style={{ boxSizing: 'border-box' }}
        />
      </Box>
    );
  };

  useEffect(() => {
    executeCall({ q: searchQuery || undefined, start, length: pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, start]);

  useEffect(() => {
    if (!data) {
      return;
    }
    if (isSearchResetRef.current) {
      setAccumulatedItems(data);
      isSearchResetRef.current = false;
    } else {
      setAccumulatedItems(prev => [...prev, ...data]);
    }
  }, [data]);

  useEffect(() => {
    if (searchable && open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchable, open]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as HTMLElement)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    setAccumulatedItems([]);
  }, []);

  return (
    <div ref={containerRef}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const elements = preparedItems.map(item => ({
            ...item,
            selected: field.value?.value === item.item.value,
          }));

          if (loadingCall && items.length === 0 && !open) {
            return (
              <WrappedInput
                name={name}
                control={control}
                value={field?.value?.label || placeholder}
                suffixSvg='loading'
                suffix='loading'
                disabled
                loading
                iconStyle={themeMode === 'dark' ? { color: 'white' } : undefined}
                label={label || ''}
              />
            );
          }

          return (
            <DropDown
              labelStyle={{ mb: 'xs', color }}
              label={alwaysShowLabel || field.value ? label : undefined}
              placeholder={field?.value?.label || placeholder}
              items={elements}
              showSvg
              dropDownHeaderStyle={
                {
                  border: hasBorderColor ? `1px solid ${parsedColor.color} !important` : undefined,
                  textStyle: { mr: 'xs' },
                  height: 44,
                  background: backgroundColorHeader || backgroundColorHeaderDefault,
                } as never
              }
              overlay
              zIndex={1000}
              maxHeight={200}
              keyExtractor={(item: SelectItem) => item.value}
              contentExtractor={contentExtractor}
              renderItem={renderItem}
              open={open}
              setOpen={() => setOpen(!open)}
              onSelect={value => {
                if (value.item.value === SEARCH_VALUE) return;
                if (value.item.value === LOAD_MORE_VALUE) {
                  handleLoadMore();
                  return;
                }
                field.onChange(value.item);
                onItemSelect?.(value.item);
                setOpen(false);
              }}
            />
          );
        }}
      />
    </div>
  );
};
